import dayjs from "dayjs";
import { encryptString } from "components/common/EncryptDecrypt";
import { getCancelPOStatusToSend } from "components/common/POUtils";
import { PO_ITEM_STATUS, PO_STATUS } from "components/common/CONSTANTS";

const isGibraltarItem = (item) => !item.casesPerLayer || !item.layerPerPallet;

const isItemEmpty = (item) =>
  isGibraltarItem(item)
    ? !item.quantityOrdered ||
      item.quantityOrdered === 0 ||
      item.quantityOrdered === "" ||
      item.quantityOrdered === null
    : !item.quantityOrdered ||
      !item.palletQuantity ||
      item.quantityOrdered === 0 ||
      item.palletQuantity === 0 ||
      item.quantityOrdered === "" ||
      item.palletQuantity === "" ||
      item.quantityOrdered === null ||
      item.palletQuantity === null;

// Validates if all dates in the amend items are valid
export const areAllDatesValid = (amendItems) => {
  const orderDate = dayjs(amendItems[0].orderDate).startOf("day");
  const deliveryDate = dayjs(amendItems[0].deliveryDate).startOf("day");
  const today = dayjs().startOf("day");
  const orderId = amendItems[0].orderId;

  if (!orderDate || !deliveryDate) {
    return false;
  }

  if (!orderId && orderDate.isAfter(deliveryDate)) {
    return false;
  }

  if (!orderId && orderDate.isBefore(today)) {
    return false;
  }

  return true;
};

// Filters and maps items that need to be updated
export const getUpdatedItems = (
  amendItems,
  rounding,
  selectedRows = [],
  originalPOStatus = "",
  allowInactiveForDraft = false,
  clickConfirm = false
) => {
  return amendItems
    ?.filter(
      (item) => {
        const isAddedItemEmpty = item.addedItem && isItemEmpty(item);
        return !isAddedItemEmpty &&
          (item.quantityOrdered !== item.originalQuantityOrdered ||
            item.palletQuantity !== item.originalPalletQuantity ||
            (item.quantityOrdered && !item.originalQuantityOrdered) ||
            (item.palletQuantity && !item.originalPalletQuantity) ||
            item.originalStatus === PO_ITEM_STATUS.DRAFT ||
            item.isQuantityRounded !== item.isQuantityRoundedOriginal ||
            selectedRows?.some(
              (row) => row.packId === item.packId && row.itemId === item.itemId
            ));
      }
    )
    .map((item) =>
      processItemForUpdate(
        item,
        rounding,
        selectedRows,
        originalPOStatus,
        allowInactiveForDraft,
        clickConfirm
      )
    )
    .filter(Boolean);
};

// Process individual item for update
export const processItemForUpdate = (
  item,
  rounding,
  selectedRows = [],
  originalPOStatus = "",
  allowInactiveForDraft = false,
  clickConfirm = false
) => {
  if (
    item.addedItem &&
    item.quantityOrdered &&
    item.palletQuantity &&
    (item.quantityOrdered !== 0 ||
      item.palletQuantity !== 0 ||
      item.quantityOrdered !== "" ||
      item.palletQuantity !== "" ||
      item.quantityOrdered !== null ||
      item.palletQuantity !== null)
  ) {
    const payloadItem = {
      ...item,
      status: PO_ITEM_STATUS.CREATE,
    };

    if (
      item.quantityOrdered !== item.originalQuantityOrdered ||
      item.isQuantityRounded !== item.isQuantityRoundedOriginal
    ) {
      payloadItem.quantityOrdered = rounding
        ? item.quantityOrderedRounded
        : item.quantityOrdered;
    }

    if (
      item.palletQuantity !== item.originalPalletQuantity ||
      item.isQuantityRounded !== item.isQuantityRoundedOriginal
    ) {
      payloadItem.palletQuantity = rounding
        ? item.palletQuantityRounded
        : item.palletQuantity;
    }

    return payloadItem;
  }

  const payloadItem = {
    packId: item.packId,
    itemId: item.itemId,
    status: item.status === PO_ITEM_STATUS.DRAFT ? PO_ITEM_STATUS.CREATE : PO_ITEM_STATUS.SOMS_AMENDED,
  };

  const isSelected = selectedRows.some(
    (selectedItem) =>
      selectedItem.packId === item.packId && selectedItem.itemId === item.itemId
  );

  if (isSelected) {
    payloadItem.status = PO_ITEM_STATUS.REMOVE;
  } else {
    const isNowEmpty = isItemEmpty(item);

    const hasNoChanges =
      item.quantityOrdered === item.originalQuantityOrdered &&
      item.palletQuantity === item.originalPalletQuantity &&
      item.isQuantityRounded === item.isQuantityRoundedOriginal;

    if (
      originalPOStatus === PO_STATUS.DRAFT &&
      item.originalStatus === PO_ITEM_STATUS.DRAFT &&
      hasNoChanges &&
      !clickConfirm
    ) {
      payloadItem.status = PO_ITEM_STATUS.DRAFT;

      payloadItem.quantityOrdered = rounding
        ? item.quantityOrderedRounded
        : item.quantityOrdered;

      payloadItem.palletQuantity = rounding
        ? item.palletQuantityRounded
        : item.palletQuantity;
    }

    if (isNowEmpty) {
      const hadOriginalValue =
        item.originalQuantityOrdered > 0 || item.originalPalletQuantity > 0;

      if (hadOriginalValue && clickConfirm) {
        payloadItem.status = PO_ITEM_STATUS.REMOVE; // was filled, now empty via confirm
      } else if (originalPOStatus === PO_STATUS.DRAFT && allowInactiveForDraft) {
        payloadItem.status = PO_ITEM_STATUS.INACTIVE; // never had value
      } else {
        return null; // skip from payload
      }
    }
  }

  // Quantity changes - skip if item is being removed
  if (payloadItem.status !== PO_ITEM_STATUS.REMOVE) {
    if (
      item.quantityOrdered !== item.originalQuantityOrdered ||
      item.isQuantityRounded !== item.isQuantityRoundedOriginal ||
      (clickConfirm && item.originalStatus === PO_ITEM_STATUS.DRAFT)
    ) {
      payloadItem.quantityOrdered = rounding
        ? item.quantityOrderedRounded
        : item.quantityOrdered;
    }

    if (
      item.palletQuantity !== item.originalPalletQuantity ||
      item.isQuantityRounded !== item.isQuantityRoundedOriginal ||
      (clickConfirm && item.originalStatus === PO_ITEM_STATUS.DRAFT)
    ) {
      payloadItem.palletQuantity = rounding
        ? item.palletQuantityRounded
        : item.palletQuantity;
    }
  }

  return payloadItem;
};

// Get updated header information
export const getUpdatedHeader = (amendItems) => {
  return amendItems
    .filter(
      (item) =>
        item.orderDate !== item.originalOrderDate ||
        item.deliveryDate !== item.originalDeliveryDate ||
        item.isHandball !== item.isHandballOriginal ||
        item.isOverseas !== item.isOverseasOriginal ||
        item.isQuantityRounded !== item.isQuantityRoundedOriginal ||
        item.comments !== item.originalComments
    )
    .map(
      ({
        orderDate,
        deliveryDate,
        originalOrderDate,
        originalDeliveryDate,
        isHandball,
        isHandballOriginal,
        isOverseas,
        isOverseasOriginal,
        isQuantityRounded,
        isQuantityRoundedOriginal,
        comments,
        originalComments,
        totalCases,
        totalPallets,
        totalBases,
        totalWagons,
      }) => {
        const payloadHeader = {};

        if (orderDate !== originalOrderDate) {
          payloadHeader.orderDate = orderDate;
        }
        if (deliveryDate !== originalDeliveryDate) {
          payloadHeader.deliveryDate = deliveryDate;
        }
        if (isHandball !== isHandballOriginal) {
          payloadHeader.isHandball = isHandball;
        }
        if (isOverseas !== isOverseasOriginal) {
          payloadHeader.isOverseas = isOverseas;
        }
        if (isQuantityRounded !== isQuantityRoundedOriginal) {
          payloadHeader.isQuantityRounded = isQuantityRounded;
        }
        if (comments && comments !== originalComments) {
          payloadHeader.userComments = comments;
        }

        payloadHeader.totalCasesCount = totalCases;
        payloadHeader.totalPalletCount = totalPallets;
        payloadHeader.totalBasesCount = totalBases;
        payloadHeader.totalWagonsCount = totalWagons;

        return payloadHeader;
      }
    )[0];
};

// Check if all quantities are zero
export const areAllQuantitiesZero = (items) => {
  return items.every((item) => {
    const qtyEmpty =
      !item.quantityOrdered ||
      item.quantityOrdered === 0 ||
      item.quantityOrdered === "" ||
      item.quantityOrdered === null;
    if (isGibraltarItem(item)) return qtyEmpty;
    const palletEmpty =
      !item.palletQuantity ||
      item.palletQuantity === 0 ||
      item.palletQuantity === "" ||
      item.palletQuantity === null;
    return qtyEmpty && palletEmpty;
  });
};

// Determine the final status for the header
export const determineHeaderStatus = (
  updatedItems,
  amendItems,
  updatedHeader,
  originalPOStatus,
  clickConfirm = false
) => {
  if (
    updatedItems.length > 0 &&
    areAllQuantitiesZero(updatedItems) &&
    updatedItems.length === amendItems.length
  ) {
    return {
      ...updatedHeader,
      status: getCancelPOStatusToSend(originalPOStatus),
    };
  } else if (originalPOStatus === PO_STATUS.DRAFT) {
    return {
      ...updatedHeader,
      status: clickConfirm ? PO_STATUS.CONFIRMED : PO_STATUS.DRAFT,
    };
  } else {
    return {
      ...updatedHeader,
      status:
        originalPOStatus === PO_STATUS.RAISED ||
        originalPOStatus === PO_STATUS.AMENDED ||
        originalPOStatus === PO_STATUS.SOMS_AMENDED
          ? PO_STATUS.SOMS_AMENDED
          : PO_STATUS.CONFIRMED,
    };
  }
};

// Create the final payload for the amendment
export const createAmendPayload = async (
  updatedHeader,
  updatedItems,
  reason,
  loginResponse
) => {
  const encryptedEmailId = await encryptString(loginResponse?.email);
  return {
    ...updatedHeader,
    ...(reason !== undefined && { reasonCode: reason }),
    items: updatedItems,
    userId: encryptedEmailId,
  };
};
